import React from "react";
import { Link } from "react-router-dom";
import LegalPage, { LegalContact } from "../components/LegalPage";
import { BUSINESS_NAME } from "../config";

// When the wording below changes, change this date too.
const UPDATED = "7 October 2026";

const SUMMARY = [
    "This website is the system we use to record milk and pay the farmers who supply us.",
    "Only people we have given a sign-in may use the parts behind the sign-in page.",
    "Staff must record truthfully and keep farmers' information confidential.",
    "If you are a farmer and think a record about you is wrong, tell us and we will check it.",
    "These terms are under the law of Kenya.",
];

const SECTIONS = [
    {
        id: "about",
        title: "About these terms",
        body: (
            <>
                <p>
                    These terms apply to this website and to the Android app our field staff use. Both are
                    run by {BUSINESS_NAME} ("we", "us"). By using them you agree to these terms and to
                    the <Link to="/privacy-policy">Privacy Policy</Link>.
                </p>
            </>
        ),
    },
    {
        id: "the-system",
        title: "What this system is",
        body: (
            <p>
                We buy milk from dairy farmers and pay them for it. With this system we keep our farmers and
                collection routes, record the milk collected, set the milk price, work out what each farmer
                is owed, and record cash advances, goods given on credit, milk sales and expenses. The public
                pages give general information; everything else is behind the sign-in page.
            </p>
        ),
    },
    {
        id: "who-may-sign-in",
        title: "Who may sign in",
        body: (
            <ul>
                <li>Only people we have given a sign-in may use the system. There is no public registration.</li>
                <li>A sign-in belongs to one person. Do not share it or let anyone else use it.</li>
                <li>Keep your password secret, and change it if you think someone else knows it.</li>
                <li>Tell us at once if you think an account has been used without permission.</li>
                <li>Do not try to sign in to an account that is not yours, or to get around the sign-in.</li>
            </ul>
        ),
    },
    {
        id: "staff",
        title: "Rules for people with a sign-in",
        body: (
            <ul>
                <li>Use the system only for the work of {BUSINESS_NAME}.</li>
                <li>Record what really happened: the right farmer, the right litres, the right amounts.</li>
                <li>Farmers' details and payments are confidential. Do not copy, share or take them outside your work.</li>
                <li>You are responsible for what is done under your sign-in.</li>
                <li>Do not try to break, overload or change the system, and do not copy the software.</li>
                <li>We may remove a sign-in when a person leaves or breaks these rules.</li>
            </ul>
        ),
    },
    {
        id: "farmers",
        title: "Farmers and their records",
        body: (
            <>
                <ul>
                    <li>Farmers do not sign in. Our staff record your details, your milk and your payments.</li>
                    <li>What you are owed is worked out from the milk recorded, the milk price and any advance or credit recorded against you.</li>
                    <li>If you think a record about you is wrong, tell us as soon as you can. We will check it and correct it if it is wrong.</li>
                </ul>
                <p>
                    The price of milk, when you are paid and the other terms on which you supply us are
                    agreed between you and us. These website terms do not change them.
                </p>
            </>
        ),
    },
    {
        id: "the-app",
        title: "The Android app",
        body: (
            <ul>
                <li>The app is for our field staff. It works without internet: milk recorded stays on the phone until it is uploaded, so upload every day.</li>
                <li>If a phone is lost, damaged or reset, or the app is removed, before its collections are uploaded, those collections are lost. Tell us at once.</li>
                <li>Keep the phone locked and do not lend it while it holds farmers' records.</li>
            </ul>
        ),
    },
    {
        id: "website-information",
        title: "Information on the website",
        body: (
            <p>
                The public pages give general information about us. We try to keep it correct and up to date,
                but it may change, and it is not an offer or a promise.
            </p>
        ),
    },
    {
        id: "availability",
        title: "Availability and changes",
        body: (
            <p>
                We work to keep the system available and correct, but we cannot promise that it will never be
                interrupted or free of every fault. Sometimes it is taken offline for a short time to be
                maintained or improved. We may add, change or remove parts of it.
            </p>
        ),
    },
    {
        id: "ownership",
        title: "Who owns what",
        body: (
            <p>
                The content of this website and the records in the system belong to {BUSINESS_NAME}. The
                software belongs to us or to our software provider. You may not copy or reuse any of it
                without permission.
            </p>
        ),
    },
    {
        id: "responsibility",
        title: "Our responsibility",
        body: (
            <>
                <p>
                    We run the system with reasonable care. As far as the law allows, we are not responsible
                    for loss caused by someone misusing a sign-in, by using the system against these terms,
                    or by things outside our control, such as failures of the internet, electricity, phones,
                    M-Pesa or banks.
                </p>
                <p>Nothing in these terms takes away a right that the law gives you and that cannot be removed by agreement.</p>
            </>
        ),
    },
    {
        id: "changes",
        title: "Changes to these terms",
        body: (
            <p>
                When these terms change we put the new version on this page with a new date. If you keep
                using the system after that, you accept the new terms.
            </p>
        ),
    },
    {
        id: "law",
        title: "Law and disagreements",
        body: (
            <p>
                These terms are under the law of Kenya. If you have a complaint, talk to us first and we will
                try to settle it with you. If that fails, the courts of Kenya decide.
            </p>
        ),
    },
    {
        id: "contact",
        title: "Contact us",
        body: (
            <>
                <p>For any question about these terms:</p>
                <LegalContact />
            </>
        ),
    },
];

function TermsAndConditions() {
    return (
        <LegalPage
            title="Terms and Conditions"
            intro="What applies when you use this website and the system behind it."
            updated={UPDATED}
            summary={SUMMARY}
            sections={SECTIONS}
            other={{ to: "/privacy-policy", label: "Privacy Policy" }}
        />
    );
}

export default TermsAndConditions;
